import React, { useState, useRef, useEffect } from 'react';
import { theme } from '../theme';
import Icons from './Icons';

// ---------------------------------------------------------------------------
// TabBar — horizontal tab strip for the SQL editor
// Double-click a tab name to rename it inline.
// ---------------------------------------------------------------------------
export default function TabBar({ tabs, activeTabId, onTabSelect, onTabAdd, onTabClose, onTabRename, onTabReorder, onTabSave, canSave }) {
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [dragSourceId, setDragSourceId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (editingId) inputRef.current?.select();
  }, [editingId]);

  const startRename = (tab, e) => {
    e.stopPropagation();
    setEditingId(tab.id);
    setEditingName(tab.name);
  };

  const commitRename = () => {
    if (editingName.trim()) onTabRename(editingId, editingName.trim());
    setEditingId(null);
  };

  return (
    <div style={{
      display: 'flex',
      alignItems: 'stretch',
      background: theme.bg,
      borderBottom: `1px solid ${theme.border}`,
      flexShrink: 0,
      height: '34px',
      overflow: 'hidden',
    }}>
      {/* Scrollable tab list */}
      <div
        ref={scrollRef}
        style={{
          display: 'flex',
          alignItems: 'stretch',
          overflowX: 'auto',
          flex: 1,
          scrollbarWidth: 'none',
        }}
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <div
              key={tab.id}
              draggable={editingId !== tab.id}
              onClick={() => onTabSelect(tab.id)}
              onDoubleClick={(e) => startRename(tab, e)}
              onDragStart={() => setDragSourceId(tab.id)}
              onDragEnd={() => { setDragSourceId(null); setDragOverId(null); }}
              onDragOver={(e) => { e.preventDefault(); setDragOverId(tab.id); }}
              onDragLeave={() => setDragOverId(null)}
              onDrop={(e) => {
                e.preventDefault();
                if (dragSourceId && dragSourceId !== tab.id) onTabReorder(dragSourceId, tab.id);
                setDragSourceId(null);
                setDragOverId(null);
              }}
              title="Double-click to rename"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '0 8px 0 12px',
                cursor: dragSourceId ? 'grabbing' : 'grab',
                fontSize: '12px',
                color: isActive ? theme.text : theme.textMuted,
                background: isActive ? theme.surface : 'transparent',
                borderRight: `1px solid ${theme.border}`,
                borderBottom: isActive ? `2px solid ${theme.accent}` : '2px solid transparent',
                borderLeft: dragOverId === tab.id && dragSourceId !== tab.id
                  ? `2px solid ${theme.accent}` : '2px solid transparent',
                whiteSpace: 'nowrap',
                maxWidth: '180px',
                minWidth: '90px',
                userSelect: 'none',
                flexShrink: 0,
                opacity: dragSourceId === tab.id ? 0.4 : 1,
                transition: 'background 0.1s, color 0.1s, opacity 0.1s',
              }}
              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = theme.surfaceHover; }}
              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
            >
              {editingId === tab.id ? (
                <input
                  ref={inputRef}
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={commitRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                    if (e.key === 'Escape') setEditingId(null);
                    e.stopPropagation();
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `1px solid ${theme.accent}`,
                    color: theme.text,
                    fontSize: '12px',
                    width: '90px',
                    outline: 'none',
                    padding: 0,
                    fontFamily: 'inherit',
                  }}
                />
              ) : (
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1,
                  minWidth: 0,
                }}>
                  {tab.name}
                </span>
              )}

              {/* Save / saved indicator — active tab only */}
              {isActive && (() => {
                const cleanSaved = tab.savedQueryId && tab.sql === (tab.savedSql || '');
                const needsSave  = canSave && !cleanSaved;
                if (cleanSaved) {
                  return (
                    <span title="Saved" style={{
                      display: 'flex', alignItems: 'center', padding: '2px', flexShrink: 0,
                      color: theme.success || '#4ade80',
                    }}>
                      <Icons.Check style={{ width: 11, height: 11 }} />
                    </span>
                  );
                }
                return (
                  <button
                    onClick={(e) => { e.stopPropagation(); if (needsSave) onTabSave(); }}
                    title={tab.savedQueryId ? 'Modified — save again' : 'Save query'}
                    style={{
                      background: 'none', border: 'none', padding: '2px',
                      display: 'flex', alignItems: 'center',
                      borderRadius: '3px', flexShrink: 0,
                      cursor: needsSave ? 'pointer' : 'default',
                      color: needsSave ? theme.accent : theme.textMuted,
                      opacity: needsSave ? 0.75 : 0.3,
                      transition: 'opacity 0.1s, color 0.1s',
                    }}
                    onMouseEnter={(e) => { if (needsSave) e.currentTarget.style.opacity = 1; }}
                    onMouseLeave={(e) => { if (needsSave) e.currentTarget.style.opacity = 0.75; }}
                  >
                    <Icons.Save style={{ width: 11, height: 11 }} />
                  </button>
                );
              })()}

              {/* Close button — hidden on last tab */}
              {(
                <button
                  onClick={(e) => { e.stopPropagation(); onTabClose(tab.id); }}
                  title="Close tab"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: theme.textMuted,
                    padding: '2px',
                    display: 'flex', alignItems: 'center',
                    borderRadius: '3px',
                    flexShrink: 0,
                    opacity: 0.5,
                    transition: 'opacity 0.1s, background 0.1s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = 1;
                    e.currentTarget.style.background = `${theme.danger}33`;
                    e.currentTarget.style.color = theme.danger;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = 0.5;
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = theme.textMuted;
                  }}
                >
                  <Icons.X style={{ width: 10, height: 10 }} />
                </button>
              )}
            </div>
          );
        })}

        {/* Add tab button — sits right after the last tab */}
        <button
          onClick={onTabAdd}
          title="New tab"
          style={{
            background: 'none',
            border: 'none',
            borderRight: `1px solid ${theme.border}`,
            cursor: 'pointer',
            color: theme.textMuted,
            padding: '0 10px',
            fontSize: '18px',
            lineHeight: 1,
            flexShrink: 0,
            display: 'flex', alignItems: 'center',
            transition: 'color 0.1s, background 0.1s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = theme.text;
            e.currentTarget.style.background = theme.surfaceHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = theme.textMuted;
            e.currentTarget.style.background = 'transparent';
          }}
        >
          +
        </button>
      </div>
    </div>
  );
}
